import {
    createStore,
    ObservableMap,
} from '@stencil/store';
import {
    get,
    getEnvironment,
} from '@uipath/portal-shell-util';

import { STORAGE_KEYS } from '../../constants/constants';
import { ISessionStoreState } from '../../models/sessionStoreState';
import { shouldRefetch } from '../api/refetchThrottleHelper';
import {
    getEnvConfigField,
    isMsiOrStandalone,
} from '../EnvironmentUtil';
import { GlobalPropsInstanceResolver } from '../globalProps/globalPropsInstanceResolver';
import { sanitizeToken } from '../helper';
import Logger from '../logger/logger';
import { getOrganizationStandardUrl } from '../orgStandardUrlHelper';
import {
    getAccountName,
    getSpaceName,
} from '../urlHelper';
import {
    DefaultValuesMap,
    Features,
} from './featureFlagConstants';

export default class FeatureFlags<T extends boolean | Object | number = boolean> {
    private static cacheKeyPrefix = STORAGE_KEYS.PORTAL_FEATURE_FLAGS;
    private static featureFlagsLastRefetchedKeyPrefix = STORAGE_KEYS.PORTAL_SHELL_FLAGS_LAST_REFETCHED;

    private flagStore: ObservableMap<{ [key: string]: T }>;

    constructor(flagStore: ObservableMap<{ [key: string]: T }>) {
        this.flagStore = flagStore;
        Object
            .entries(FeatureFlags.getCachedFeatureFlags())
            .forEach(([ key, value ]: [string, T]) => {
                this.flagStore.set(key, value);
            });
    }

    static getDefaultFeatureFlags() {
        return {
            ...DefaultValuesMap,
            ...(getEnvConfigField?.('FeatureFlags') || {}),
            ...(GlobalPropsInstanceResolver?.Instance?.featureFlagOverrides ?? {}),
        };
    }

    async fetchFeatureFlags(sessionStorage: ObservableMap<ISessionStoreState>, token = '') {
        if (isMsiOrStandalone()) {
            Logger.debug('featureFlags', 'msi or standalone, skipping fetching feature flags');
            return;
        }

        const {
            refetchCondition, setLocalStorageLastRefetched,
        } = shouldRefetch(Features.FeatureFlagsRefetchThrottleMs.name, `${FeatureFlags.featureFlagsLastRefetchedKeyPrefix}_${getAccountName()}`);
        if ((this.getFeatureFlag(Features.EnableStatsigAbTesting.name) && !token) || !refetchCondition) {
            Logger.debug('featureFlags', `skipping fetch due to throttle or missing token`);
            return;
        }

        Logger.debug('featureFlags', `fetch: token='${sanitizeToken(token)}'`);
        const fetchedFlags = await get<{ [key: string]: { value: T; source: string } }>(
            `${getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true })}/portal_/api/featureFlags?filter=portal_shell&spaceName=${getSpaceName()}`,
            {
                token,
                headers: {
                    'X-UiPath-Internal-AccountName': getAccountName() ?? '',
                    'X-UiPath-Internal-AccountId': sessionStorage.get('organizationId'),
                },
            },
            (e) => {
                Logger.warn([ 'featureFlags', 'api' ], `unexpected error: ${e}`, { stack: e.stack });
                return {};
            },
        );
        setLocalStorageLastRefetched();

        const processedFlags = fetchedFlags ? Object.entries(fetchedFlags).reduce((accumulator, [ key, value ]) => {
            accumulator[key] = value?.value;
            return accumulator;
        }, {}) : {};
        const localStorageOverride = fetchedFlags?.[Features.EnablePortalShellLocalStorageOverrides.name]?.value as boolean;
        const localStorageFlags = getEnvConfigField('FeatureFlags', getEnvironment(), localStorageOverride) ?? {};
        const flags = {
            ...processedFlags,
            ...localStorageFlags,
            ...(GlobalPropsInstanceResolver?.Instance?.featureFlagOverrides ?? {}),
        };

        FeatureFlags.setFeatureFlagsCache(flags);
        Object.entries(flags).forEach(([ key, value ]: [string, T]) => {
            this.flagStore.set(key, value);
        });
    }

    /**
     * USE THE EXPORTED GET_FEATURE_FLAG(S) FUNCTIONS INSTEAD (getFeatureFlag(key), getFeatureFlags(keys)) for components
     * This function does not trigger rerender in components, but it does return the correct flag value at the time it is called.
     * Use this in all other cases (such as in side effects)
     * @param key string
     * @returns boolean
     */
    getFeatureFlag(key: string) {
        return this.flagStore.get(key);
    }

    /**
     * Set the override flags
     * To be used by the GlobalEventEmitter after passing in the feature flags overrides
     */
    setOverrideFlags(overrideFlags: { [key: string]: T }) {
        Object.entries(overrideFlags).forEach(([ key, value ]: [string, T]) => {
            if (this.flagStore.get(key) == null) {
                return;
            }
            this.flagStore.set(key, value);
        });
    }

    private static getCachedFeatureFlags(): any {
        const featureFlag = localStorage.getItem(FeatureFlags.getFeatureFlagsCacheKey());

        const defaultValues = FeatureFlags.getDefaultFeatureFlags();
        if (!featureFlag) {
            return defaultValues;
        }

        let cachedFeatureFlags = {};
        try {
            cachedFeatureFlags = JSON.parse(featureFlag);
        } catch {
            cachedFeatureFlags = {};
        }

        // Required feature flags (ones found in FeatureFlagConstants) must be in the cache
        const currentFlags = Object.entries(defaultValues).reduce((accumulator, current) => {
            const cachedFlagValue = accumulator[current[0]];
            if (cachedFlagValue == null) {
                accumulator[current[0]] = current[1];
            }
            return accumulator;
        }, cachedFeatureFlags);

        Logger.debug('featureFlags', `loaded cached: ${JSON.stringify(currentFlags, null, 2)}`);

        return {
            ...currentFlags,
            ...(GlobalPropsInstanceResolver?.Instance?.featureFlagOverrides ?? {}),
        };
    }

    private static setFeatureFlagsCache<T extends boolean | Object | number = boolean>(featureFlags: { [x: string]: T }) {
        localStorage.setItem(FeatureFlags.getFeatureFlagsCacheKey(), JSON.stringify(featureFlags));
    }

    private static getFeatureFlagsCacheKey() {
        const accountName = getAccountName();
        if (!accountName) {
            return FeatureFlags.cacheKeyPrefix;
        }
        return `${FeatureFlags.cacheKeyPrefix}_${accountName}`;
    }
}

export const featureFlagStore = createStore(FeatureFlags.getDefaultFeatureFlags());

export const getFeatureFlag = <T extends boolean | Object | number = boolean>(key: string): T => featureFlagStore.get(key);
export const getFeatureFlags = <T extends boolean | Object | number = boolean>(keys: string[]): T[] => keys.map(key => getFeatureFlag(key));
